/**
 * ORBIT LUXE — motion engine
 * --------------------------------------------------------------------------
 * One small, dependency-free module that powers every `olx-` section:
 *
 *   1. Reveal          [data-reveal] / [data-lines] / [data-reveal-group]
 *   2. Parallax        [data-parallax="speed"]  → --py   (scroll-linked)
 *   3. Pointer tilt    [data-tilt-scene] > [data-tilt="depth"] → --tx/--ty
 *   4. Pinned showcase [data-olx-showcase] horizontal track driven by scroll
 *   5. Counters        [data-count="123"]
 *   6. Magnetic CTAs   [data-magnetic]
 *   7. Media           img[data-fade] load fades, [data-olx-video] loader
 *   8. Header state    body.olx-scrolled
 *
 * Everything is transform/opacity only, runs inside a single rAF loop that
 * sleeps when nothing is moving, and is fully disabled for
 * prefers-reduced-motion (reveals become instant).
 */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var body = doc.body;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var settings = window.OrbitLuxeSettings || {};
  var parallaxEnabled = settings.parallax !== false && !reduceMotion;
  var tiltEnabled = settings.pointerParallax !== false && !reduceMotion && finePointer;

  /* orbit-luxe.js has arrived — cancel the no-JS safety timer set in <head>. */
  if (window.__olxRevealTimer) {
    clearTimeout(window.__olxRevealTimer);
    window.__olxRevealTimer = null;
  }

  var vh = window.innerHeight;
  var vw = window.innerWidth;
  var scrollY = window.scrollY;

  /* ------------------------------------------------------------------ *
   * Frame loop — subscribers register a `tick()` that returns true while
   * it still needs frames. The loop sleeps when everybody is settled.
   * ------------------------------------------------------------------ */
  var tasks = [];
  var frame = null;
  var needsFrame = false;

  function requestFrame() {
    needsFrame = true;
    if (frame === null) frame = window.requestAnimationFrame(loop);
  }

  function loop() {
    frame = null;
    needsFrame = false;
    var busy = false;
    for (var i = 0; i < tasks.length; i++) {
      try {
        if (tasks[i]()) busy = true;
      } catch (e) {
        /* never let one section break the loop */
      }
    }
    if (busy || needsFrame) frame = window.requestAnimationFrame(loop);
  }

  function addTask(fn) {
    tasks.push(fn);
    requestFrame();
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  window.addEventListener(
    'scroll',
    function () {
      scrollY = window.scrollY;
      requestFrame();
    },
    { passive: true }
  );

  window.addEventListener(
    'resize',
    function () {
      vh = window.innerHeight;
      vw = window.innerWidth;
      scrollY = window.scrollY;
      doc.dispatchEvent(new CustomEvent('olx:resize'));
      requestFrame();
    },
    { passive: true }
  );

  /* ------------------------------------------------------------------ *
   * 1. Reveal
   * ------------------------------------------------------------------ */
  var revealSelector = '[data-reveal], [data-lines]';
  var revealObserver = null;

  function settle(el) {
    el.classList.add('is-settled');
  }

  /* Settle only after the longest reveal transition (1.4s + stagger + delay) */
  function settleDelay(el) {
    var cs = window.getComputedStyle(el);
    var i = parseFloat(cs.getPropertyValue('--i')) || 0;
    var d = (cs.getPropertyValue('--reveal-delay') || '').trim();
    var extra = d ? (/ms$/.test(d) ? parseFloat(d) : parseFloat(d) * 1000) : 0;
    return 1800 + i * 110 + (extra || 0);
  }

  function markInView(el) {
    if (el.classList.contains('is-inview')) return;
    el.classList.add('is-inview');
    window.setTimeout(settle.bind(null, el), settleDelay(el));
  }

  function observeReveal(el) {
    if (el.classList.contains('is-inview')) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      markInView(el);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          for (var e = 0; e < entries.length; e++) {
            if (entries[e].isIntersecting) {
              markInView(entries[e].target);
              revealObserver.unobserve(entries[e].target);
            }
          }
        },
        { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
      );
    }
    var r = el.getBoundingClientRect();
    if (r.top < vh * 0.92 && r.bottom > 0) {
      markInView(el);
    } else {
      revealObserver.observe(el);
    }
  }

  function initReveal(scope) {
    scope = scope || doc;

    /* Groups: stagger children automatically via --i */
    var groups = scope.querySelectorAll('[data-reveal-group]');
    for (var g = 0; g < groups.length; g++) {
      var kids = groups[g].children;
      for (var k = 0; k < kids.length; k++) {
        if (!kids[k].hasAttribute('data-reveal')) kids[k].setAttribute('data-reveal', groups[g].getAttribute('data-reveal-group') || '');
        kids[k].style.setProperty('--i', String(k));
      }
    }

    var els = scope.querySelectorAll(revealSelector);
    for (var j = 0; j < els.length; j++) {
      /* Line-split headlines are observed by initLines once split */
      if (els[j].hasAttribute('data-lines') && els[j].dataset.split !== 'done') continue;
      observeReveal(els[j]);
    }
  }

  /* Split a headline into line masks (based on rendered line boxes). */
  function splitLines(el) {
    if (!el.dataset.originalHtml) el.dataset.originalHtml = el.innerHTML;
    var html = el.dataset.originalHtml;

    /* Tokenise words, remembering <em> context */
    var tokens = [];
    var tmp = doc.createElement('div');
    tmp.innerHTML = html;
    (function walk(node, italic) {
      for (var n = node.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3) {
          var parts = n.textContent.split(/(\s+)/);
          for (var p = 0; p < parts.length; p++) {
            if (parts[p].trim()) tokens.push({ text: parts[p], italic: italic });
          }
        } else if (n.nodeType === 1) {
          if (n.tagName === 'BR') tokens.push({ br: true });
          else walk(n, italic || n.tagName === 'EM' || n.tagName === 'I');
        }
      }
    })(tmp, false);
    if (!tokens.length) return;

    /* Pass 1: render words inline-block to read their line boxes */
    el.innerHTML = '';
    var spans = [];
    for (var t = 0; t < tokens.length; t++) {
      if (tokens[t].br) {
        el.appendChild(doc.createElement('br'));
        spans.push(null);
        continue;
      }
      var w = doc.createElement('span');
      w.style.display = 'inline-block';
      w.textContent = tokens[t].text;
      w.__italic = tokens[t].italic;
      if (tokens[t].italic) {
        var em = doc.createElement('em');
        em.appendChild(w);
        el.appendChild(em);
      } else {
        el.appendChild(w);
      }
      el.appendChild(doc.createTextNode(' '));
      spans.push(w);
    }

    var lines = [];
    var lastTop = null;
    for (var s = 0; s < spans.length; s++) {
      if (spans[s] === null) {
        lastTop = null;
        continue;
      }
      var top = spans[s].offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 4) {
        lines.push([]);
        lastTop = top;
      }
      lines[lines.length - 1].push(spans[s]);
    }

    /* Pass 2: rebuild as masked lines */
    el.innerHTML = '';
    for (var l = 0; l < lines.length; l++) {
      var mask = doc.createElement('span');
      mask.className = 'olx-line';
      var inner = doc.createElement('span');
      inner.className = 'olx-line__in';
      inner.style.setProperty('--i', String(l));
      for (var x = 0; x < lines[l].length; x++) {
        var word = lines[l][x];
        var node = doc.createTextNode(word.textContent + (x < lines[l].length - 1 ? ' ' : ''));
        if (word.__italic) {
          var em2 = doc.createElement('em');
          em2.appendChild(node);
          inner.appendChild(em2);
        } else {
          inner.appendChild(node);
        }
      }
      mask.appendChild(inner);
      el.appendChild(mask);
    }
    el.dataset.split = 'done';
    el.dataset.lineCount = String(lines.length);
  }

  var fontsReady = (function () {
    if (!(doc.fonts && doc.fonts.ready)) return Promise.resolve();
    return Promise.race([
      doc.fonts.ready,
      new Promise(function (res) { window.setTimeout(res, 1400); }),
    ]);
  })();

  function initLines(scope) {
    var heads = (scope || doc).querySelectorAll('[data-lines]');
    if (!heads.length) return;
    if (reduceMotion) {
      for (var i = 0; i < heads.length; i++) heads[i].classList.add('is-inview');
      return;
    }
    fontsReady.then(function () {
      for (var i = 0; i < heads.length; i++) {
        splitLines(heads[i]);
        observeReveal(heads[i]);
      }
    });
  }

  /* Line boxes change with width: re-split (already-revealed heads stay visible) */
  var lastSplitWidth = window.innerWidth;
  doc.addEventListener('olx:resize', function () {
    if (Math.abs(vw - lastSplitWidth) < 24) return;
    lastSplitWidth = vw;
    var heads = doc.querySelectorAll('[data-lines][data-split="done"]');
    for (var i = 0; i < heads.length; i++) splitLines(heads[i]);
  });

  /* ------------------------------------------------------------------ *
   * 2. Parallax (scroll-linked, viewport-gated)
   * ------------------------------------------------------------------ */
  var parallaxItems = [];

  function measureParallax() {
    for (var i = 0; i < parallaxItems.length; i++) {
      var it = parallaxItems[i];
      var host = it.host;
      var r = host.getBoundingClientRect();
      it.top = r.top + scrollY;
      it.height = r.height || 1;
    }
  }

  function initParallax(scope) {
    if (!parallaxEnabled) return;
    var els = (scope || doc).querySelectorAll('[data-parallax]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.olxParallaxBound) continue;
      el.dataset.olxParallaxBound = '1';
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.2;
      var scaleAttr = el.getAttribute('data-parallax-scale');
      var host = el.closest('[data-parallax-host]') || el.parentElement || el;
      /* Clipped media needs scale ≥ 1 + |speed| so edges never show */
      if (scaleAttr !== 'none') {
        var scale = parseFloat(scaleAttr) || 1 + Math.abs(speed) * 1.06;
        el.style.setProperty('--ps', scale.toFixed(3));
      }
      parallaxItems.push({ el: el, host: host, speed: speed, top: 0, height: 1, cur: 0, target: 0 });
    }
    measureParallax();
    if (parallaxItems.length) addTask(parallaxTick);
  }

  function parallaxTick() {
    var busy = false;
    var center = scrollY + vh / 2;
    for (var i = 0; i < parallaxItems.length; i++) {
      var it = parallaxItems[i];
      var hostCenter = it.top + it.height / 2;
      var visible = it.top < scrollY + vh + 200 && it.top + it.height > scrollY - 200;
      if (!visible) continue;
      /* travel ∈ [-0.5, 0.5] while the host crosses the viewport */
      var travel = clamp((center - hostCenter) / (vh + it.height), -0.5, 0.5);
      it.target = travel * it.speed * it.height;
      it.cur = lerp(it.cur, it.target, 0.16);
      if (Math.abs(it.cur - it.target) > 0.05) busy = true;
      it.el.style.setProperty('--py', it.cur.toFixed(2) + 'px');
    }
    return busy;
  }

  doc.addEventListener('olx:resize', measureParallax);

  /* ------------------------------------------------------------------ *
   * 3. Pointer tilt — parent [data-tilt-scene], children [data-tilt="depth"]
   * ------------------------------------------------------------------ */
  var tiltScenes = [];

  function initTilt(scope) {
    if (!tiltEnabled) return;
    var scenes = (scope || doc).querySelectorAll('[data-tilt-scene]');
    for (var i = 0; i < scenes.length; i++) {
      var scene = scenes[i];
      if (scene.dataset.olxTiltBound) continue;
      scene.dataset.olxTiltBound = '1';
      var layers = scene.querySelectorAll('[data-tilt]');
      var state = { scene: scene, layers: layers, x: 0, y: 0, tx: 0, ty: 0, active: false };
      tiltScenes.push(state);
      scene.addEventListener(
        'pointermove',
        (function (st) {
          return function (ev) {
            var r = st.scene.getBoundingClientRect();
            st.tx = (ev.clientX - r.left) / r.width - 0.5;
            st.ty = (ev.clientY - r.top) / r.height - 0.5;
            st.active = true;
            requestFrame();
          };
        })(state)
      );
      scene.addEventListener(
        'pointerleave',
        (function (st) {
          return function () {
            st.tx = 0;
            st.ty = 0;
            requestFrame();
          };
        })(state)
      );
    }
    if (tiltScenes.length) addTask(tiltTick);
  }

  function tiltTick() {
    var busy = false;
    for (var i = 0; i < tiltScenes.length; i++) {
      var st = tiltScenes[i];
      if (!st.active) continue;
      st.x = lerp(st.x, st.tx, 0.08);
      st.y = lerp(st.y, st.ty, 0.08);
      if (Math.abs(st.x - st.tx) > 0.0005 || Math.abs(st.y - st.ty) > 0.0005) busy = true;
      else if (st.tx === 0 && st.ty === 0) st.active = false;
      for (var l = 0; l < st.layers.length; l++) {
        var depth = parseFloat(st.layers[l].getAttribute('data-tilt')) || 20;
        st.layers[l].style.setProperty('--tx', (st.x * depth).toFixed(2) + 'px');
        st.layers[l].style.setProperty('--ty', (st.y * depth).toFixed(2) + 'px');
      }
    }
    return busy;
  }

  /* ------------------------------------------------------------------ *
   * 4. Pinned horizontal showcase
   * ------------------------------------------------------------------ */
  var showcases = [];

  function initShowcase(scope) {
    var els = (scope || doc).querySelectorAll('[data-olx-showcase]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.olxShowcaseBound) continue;
      el.dataset.olxShowcaseBound = '1';
      var st = {
        el: el,
        track: el.querySelector('[data-olx-track]'),
        bar: el.querySelector('.olx-progress__bar'),
        counter: el.querySelector('[data-olx-counter]'),
        cur: 0,
        target: 0,
        max: 0,
        top: 0,
        pinned: false,
      };
      if (!st.track) continue;
      showcases.push(st);
      measureShowcase(st);
      /* Mobile / narrow: native horizontal scroll updates the progress bar */
      st.track.addEventListener(
        'scroll',
        (function (s) {
          return function () {
            if (s.pinned) return;
            var maxScroll = s.track.scrollWidth - s.track.clientWidth;
            setProgress(s, maxScroll > 0 ? s.track.scrollLeft / maxScroll : 0);
          };
        })(st),
        { passive: true }
      );
    }
    if (showcases.length) addTask(showcaseTick);
  }

  function setProgress(st, p) {
    p = clamp(p, 0, 1);
    if (st.bar) st.bar.style.setProperty('--progress', p.toFixed(4));
    if (st.counter) {
      var cards = st.track.querySelectorAll('[data-olx-card]');
      if (cards.length) {
        var idx = Math.min(cards.length, Math.floor(p * (cards.length - 0.001)) + 1);
        st.counter.textContent = (idx < 10 ? '0' : '') + idx + ' / ' + (cards.length < 10 ? '0' : '') + cards.length;
      }
    }
  }

  function measureShowcase(st) {
    var pinned = vw >= 1024 && parallaxEnabled;
    st.pinned = pinned;
    st.el.classList.toggle('is-pinned', pinned);
    if (!pinned) {
      st.el.style.removeProperty('--olx-track-scroll');
      st.track.style.removeProperty('--olx-x');
      return;
    }
    st.max = Math.max(0, st.track.scrollWidth - vw);
    st.el.style.setProperty('--olx-track-scroll', st.max + 'px');
    var r = st.el.getBoundingClientRect();
    st.top = r.top + scrollY;
  }

  function showcaseTick() {
    var busy = false;
    for (var i = 0; i < showcases.length; i++) {
      var st = showcases[i];
      if (!st.pinned || st.max <= 0) continue;
      var progress = clamp((scrollY - st.top) / st.max, 0, 1);
      st.target = progress * st.max;
      st.cur = lerp(st.cur, st.target, 0.14);
      if (Math.abs(st.cur - st.target) > 0.2) busy = true;
      st.track.style.setProperty('--olx-x', (-st.cur).toFixed(2) + 'px');
      setProgress(st, st.cur / st.max);
    }
    return busy;
  }

  function measureAll() {
    for (var i = 0; i < showcases.length; i++) measureShowcase(showcases[i]);
    measureParallax();
    requestFrame();
  }

  doc.addEventListener('olx:resize', measureAll);

  /* ------------------------------------------------------------------ *
   * 5. Counters
   * ------------------------------------------------------------------ */
  function initCounters(scope) {
    var els = (scope || doc).querySelectorAll('[data-count]');
    if (!els.length) return;
    var run = function (el) {
      if (el.dataset.olxCounted) return;
      el.dataset.olxCounted = '1';
      var end = parseFloat(el.getAttribute('data-count')) || 0;
      var suffix = el.getAttribute('data-count-suffix') || '';
      var prefix = el.getAttribute('data-count-prefix') || '';
      if (reduceMotion) {
        el.textContent = prefix + end + suffix;
        return;
      }
      var start = null;
      var dur = 1600;
      var step = function (ts) {
        if (start === null) start = ts;
        var t = clamp((ts - start) / dur, 0, 1);
        var eased = 1 - Math.pow(1 - t, 4);
        el.textContent = prefix + Math.round(end * eased) + suffix;
        if (t < 1) window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) run(els[i]);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) {
            run(entries[e].target);
            io.unobserve(entries[e].target);
          }
        }
      },
      { threshold: 0.4 }
    );
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  /* ------------------------------------------------------------------ *
   * 6. Magnetic buttons (desktop, fine pointer)
   * ------------------------------------------------------------------ */
  function initMagnetic(scope) {
    if (!finePointer || reduceMotion) return;
    var els = (scope || doc).querySelectorAll('[data-magnetic]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.olxMagBound) continue;
      el.dataset.olxMagBound = '1';
      el.addEventListener('pointermove', function (ev) {
        var r = this.getBoundingClientRect();
        var strength = parseFloat(this.getAttribute('data-magnetic')) || 0.22;
        var x = (ev.clientX - (r.left + r.width / 2)) * strength;
        var y = (ev.clientY - (r.top + r.height / 2)) * strength;
        this.style.setProperty('--mx', x.toFixed(1) + 'px');
        this.style.setProperty('--my', y.toFixed(1) + 'px');
      });
      el.addEventListener('pointerleave', function () {
        this.style.setProperty('--mx', '0px');
        this.style.setProperty('--my', '0px');
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * 7. Media — image fades + lazy hero video
   * ------------------------------------------------------------------ */
  function initImages(scope) {
    var imgs = (scope || doc).querySelectorAll('img[data-fade]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { this.classList.add('is-loaded'); }, { once: true });
        img.addEventListener('error', function () { this.classList.add('is-loaded'); }, { once: true });
      }
    }
  }

  function connectionAllowsVideo() {
    var c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    if (c.effectiveType && /(^|\b)(slow-2g|2g|3g)\b/.test(c.effectiveType)) return false;
    return true;
  }

  function initVideos(scope) {
    var hosts = (scope || doc).querySelectorAll('[data-olx-video]');
    if (!hosts.length || reduceMotion || !connectionAllowsVideo()) return;
    for (var i = 0; i < hosts.length; i++) {
      (function (host) {
        if (host.dataset.olxVideoBound) return;
        host.dataset.olxVideoBound = '1';
        var sources;
        try {
          sources = JSON.parse(host.getAttribute('data-olx-video') || '[]');
        } catch (e) {
          sources = [];
        }
        if (!sources.length) return;
        var minWidth = parseInt(host.getAttribute('data-olx-video-min-width') || '0', 10);
        if (vw < minWidth) return;

        /* Pick the smallest rendition that still covers the viewport */
        var wantHeight = vw < 700 ? 480 : vw < 1400 ? 720 : 1080;
        sources.sort(function (a, b) { return (a.height || 0) - (b.height || 0); });
        var pick = null;
        for (var s = 0; s < sources.length; s++) {
          pick = sources[s];
          if ((sources[s].height || 0) >= wantHeight) break;
        }
        if (!pick) return;

        var start = function () {
          var video = doc.createElement('video');
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('muted', '');
          video.setAttribute('aria-hidden', 'true');
          video.preload = 'auto';
          video.className = host.getAttribute('data-olx-video-class') || 'olx-video';
          var src = doc.createElement('source');
          src.src = pick.url;
          if (pick.mime) src.type = pick.mime;
          video.appendChild(src);
          var reveal = function () {
            video.classList.add('is-playing');
            host.classList.add('has-video');
          };
          video.addEventListener('playing', reveal, { once: true });
          video.addEventListener('error', function () { video.remove(); }, { once: true });
          host.appendChild(video);
          var p = video.play();
          if (p && p.catch) p.catch(function () { /* poster stays */ });

          /* Pause when scrolled far away, resume when back */
          if ('IntersectionObserver' in window) {
            var io = new IntersectionObserver(function (entries) {
              for (var e = 0; e < entries.length; e++) {
                if (entries[e].isIntersecting) {
                  var pp = video.play();
                  if (pp && pp.catch) pp.catch(function () {});
                } else {
                  video.pause();
                }
              }
            }, { rootMargin: '25% 0px' });
            io.observe(host);
          }
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(start, { timeout: 1800 });
        } else {
          window.setTimeout(start, 400);
        }
      })(hosts[i]);
    }
  }

  /* ------------------------------------------------------------------ *
   * 8. Header state
   * ------------------------------------------------------------------ */
  function headerTick() {
    var scrolled = scrollY > 24;
    if (scrolled !== body.classList.contains('olx-scrolled')) body.classList.toggle('olx-scrolled', scrolled);
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function init(scope) {
    initLines(scope);
    initReveal(scope);
    initImages(scope);
    initParallax(scope);
    initTilt(scope);
    initShowcase(scope);
    initCounters(scope);
    initMagnetic(scope);
    initVideos(scope);
    requestFrame();
  }

  addTask(headerTick);
  init();
  root.classList.add('olx-ready');

  /* Re-measure once fonts + images settle (pinned heights, parallax offsets) */
  fontsReady.then(measureAll);
  window.addEventListener('load', measureAll);

  /* Theme editor: re-init sections as they are (re)rendered */
  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    measureAll();
  });

  /* Theme editor: re-init sections as they are (re)rendered */
  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  /* Curtain: drop the node once its exit animation has finished */
  var curtain = doc.getElementById('olx-curtain');
  if (curtain) {
    window.setTimeout(function () {
      if (curtain.parentNode) curtain.parentNode.removeChild(curtain);
    }, 2400);
  }

  window.OrbitLuxe = { init: init, requestFrame: requestFrame };
})();
